import { useState, useContext, useEffect, useRef } from "react";
import { ThemeContext } from "../../contexts/ThemeContext";
import PdfPreview from "../Preview/Preview";
import { env } from "../../environment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MediaPreview from "../MediaPreview/MediaPreview";
import "./RelatedFiles.css";
import { useTranslationContext } from "../../contexts/TranslationContext";
import pdfIcon from "../../assets/images/pdf.svg";
import imageIcon from "../../assets/images/jpeg.svg";
import videoIcon from "../../assets/images/mp4.svg";
import pptIcon from "../../assets/images/ppt.svg";
import defaultIcon from "../../assets/images/thumnail.svg";
import { downloadFile } from "../../utility/downloadFile";
import downloadBlueIcon from "../../assets/images/downloadBlueIcon.svg";

function RelatedFiles({ links }) {
  <ToastContainer closeButton={true} position="top-right" />
  const { isDarkMode } = useContext(ThemeContext);
  const [showMoreLinks, setShowMoreLinks] = useState(false);
  const { translations } = useTranslationContext();
  const relatedFilesText = translations["related_files"] || "Related Files";

  // PDF
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [pdfUrlFile, setPdfUrlFile] = useState(null);
  const [fileName, setFileName] = useState(null);

  // Media
  const [isMediaPreviewOpen, setIsMediaPreviewOpen] = useState(false);
  const [mediaUrlFile, setMediaUrlFile] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const mediaLoadError = translations["media_load_error"] || "You are offline! Cannot load the media.";
  const viewMore = translations["view_more"] || "View more";
  const viewLess = translations["view_less"] || "View less";

  // Responsive cards per row
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(4);

  const handleDownload = async (e, link) => {
    e.stopPropagation();
    try {
      const fileUrl = `${env.baseURL}/proxy/file?url=${link.file_url}`;
      await downloadFile({ url: fileUrl, filename: link.file_name });
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  useEffect(() => {
    const calculateCardsPerRow = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const card = containerRef.current.querySelector(".related-link");
        if (card) {
          const cardWidth = card.offsetWidth;
          const count = Math.floor(containerWidth / cardWidth);
          setCardsPerRow(count || 1);
        }
      }
    };

    calculateCardsPerRow();
    window.addEventListener("resize", calculateCardsPerRow);
    return () => window.removeEventListener("resize", calculateCardsPerRow);
  }, []);

  const handleLinkClick = (e, link) => {
    e.preventDefault();
    const lower = link.file_name?.toLowerCase();
    const fileUrl = `${env.baseURL}/proxy/file?url=${link.file_url}`;

    if (lower.endsWith(".pdf")) {
      setPdfUrlFile(fileUrl);
      setFileName(link.file_name);
      setIsPdfPreviewOpen(true);
    } else if (lower.endsWith(".mp4")) {
      if (!navigator.onLine) {
        toast.error(mediaLoadError);
        return;
      }
      setMediaUrlFile(fileUrl);
      setMediaType("video");
      setIsMediaPreviewOpen(true);
    } else if (
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      lower.endsWith(".png")
    ) {
      if (!navigator.onLine) {
        toast.error(mediaLoadError);
        return;
      }
      setMediaUrlFile(fileUrl);
      setMediaType("image");
      setImageLoading(true);
      setIsMediaPreviewOpen(true);
    } else if (lower.endsWith(".ppt") || lower.endsWith(".pptx")) {
      if (!navigator.onLine) {
        toast.error(mediaLoadError);
        return;
      }
      window.open(fileUrl, "_blank");
    } else {
      window.open(fileUrl, "_blank");
    }
  };

  if (!links?.length) return null;

  const visibleLinks = showMoreLinks ? links : links.slice(0, cardsPerRow);

  const getFileIcon = (fileName = "") => {
    const lower = fileName.toLowerCase();
    if (lower.endsWith(".pdf")) return pdfIcon;
    if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png")) return imageIcon;
    if (lower.endsWith(".mp4")) return videoIcon;
    if (lower.endsWith(".pptx") || lower.endsWith(".ppt")) return pptIcon;
    return defaultIcon;
  };

  return (
    <div className="relevant-links-container links-margin-top">
      <div className={`links-header ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <strong>{relatedFilesText}:</strong>
      </div>

          <div
        ref={containerRef}
        className={`related-list-main ${showMoreLinks ? "scrollable" : ""}`}
      >
        {visibleLinks.map((link, index) => (
         <div key={index} className="related-link">
            <div
              className="file-info"
              onClick={(e) => handleLinkClick(e, link)}
              role="button"
              tabIndex={0}
            >
              <img
                src={getFileIcon(link.file_name)}
                alt="file-icon"
                className="file-icon"
              />
              <span className="file-name" title={link.file_name}>
                {link.file_name}
              </span>
            </div>

            <button
              type="button"
              className="download-btn button-style"
              title="Download"
              onClick={(e) => handleDownload(e, link)}
            >
              <img src={downloadBlueIcon} alt="download-btn" />
            </button>
          </div>
        ))}
      </div>
      {links.length > cardsPerRow && (
        <div className="view-container">
          <button
            type="button"
            className="see-more"
            onClick={() => setShowMoreLinks((prev) => !prev)}
          >
            {showMoreLinks ? viewLess : viewMore}
          </button>
        </div>
      )}

      {/* PDF Preview */}
      {isPdfPreviewOpen && pdfUrlFile && (
        <PdfPreview
          pdfUrlFile={pdfUrlFile}
          isOpen={isPdfPreviewOpen}
          fileName={fileName}
          onClose={() => setIsPdfPreviewOpen(false)}
          isSummary={false}
        />
      )}

      {/* Unified Media Preview */}
      <MediaPreview
        type={mediaType}
        src={mediaUrlFile}
        isOpen={isMediaPreviewOpen}
        onClose={() => setIsMediaPreviewOpen(false)}
        loading={imageLoading}
        setLoading={setImageLoading}
      />
    </div>
  );
}

export default RelatedFiles;
